/**
 * Canvas filters (Design tab, nothing selected → Canvas filters).
 *
 * A canvas filter is a column of an entity tested with an operator - the same
 * operators as a dropdown's option filter - and it filters only the widgets and
 * texts it targets. The runtime sends it with every request that loads one of
 * them as `canvasFilters` (a request's targets: `targetCanvasFilters`), which the
 * backend applies to that computation only and never stores with the component,
 * so a filter changed or removed on the canvas leaves nothing behind.
 */

import type { LabelValueOption } from '../../shared/self-service-config.types';
import { withEmptyFilterOperators } from '../../components/dropdown-step/dropdown-options.utils';
import type { CanvasFilter } from '../design-builder.types';

/** Equals / Not equals / Contains / In / Is (not) empty. */
export const CANVAS_FILTER_OPERATORS: LabelValueOption[] = withEmptyFilterOperators();

/** One filter condition as the compute API reads it. */
export interface CanvasFilterCondition {
  dataset: string;
  field: string;
  operator: string;
  value: unknown;
}

export function createCanvasFilter(): CanvasFilter {
  return { id: `cf_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`, entity: '', field: '', operator: 'eq', values: [], targets: [] };
}

/** Whether the operator tests no value (is empty / is not empty). */
export function isValuelessOperator(operator: string | undefined): boolean {
  const op = (operator ?? '').trim().toLowerCase();
  return op === 'empty' || op === 'not_empty';
}

/**
 * The operator and value the query builder reads, or null when the filter has
 * nothing to test. "null" typed as the value means empty, as in a dropdown's
 * option filter: Equals null = is empty, Not equals null = is not empty.
 */
export function canvasFilterPredicate(filter: Pick<CanvasFilter, 'operator' | 'values'>): { operator: string; value: unknown } | null {
  let op = (filter.operator || 'eq').trim().toLowerCase();
  const values = (filter.values ?? []).map((value) => String(value ?? '').trim()).filter(Boolean);
  if (values.length === 1 && values[0].toLowerCase() === 'null') {
    if (op === 'eq') op = 'empty';
    else if (op === 'neq') op = 'not_empty';
  }
  if (op === 'empty') return { operator: 'EMPTY', value: null };
  if (op === 'not_empty') return { operator: 'NOT_EMPTY', value: null };
  if (!values.length) return null;
  switch (op) {
    case 'in': return { operator: 'IN', value: values };
    case 'neq': return { operator: '<>', value: values[0] };
    case 'contains': return { operator: 'LIKE', value: `%${values[0]}%` };
    default: return { operator: '=', value: values[0] };
  }
}

/** On, with an entity, a column, a target and something to test. */
export function isCanvasFilterActive(filter: CanvasFilter): boolean {
  return !filter.disabled && !!filter.entity?.trim() && !!filter.field?.trim() && !!filter.targets?.length && !!canvasFilterPredicate(filter);
}

/**
 * The filter's conditions for a component reading `datasets`: one for its
 * entity and one for each dataset of the component. The backend applies a
 * condition only to a query on that dataset that has the column, so the same
 * column on the component's own dataset is filtered too and a dataset without
 * it is left alone.
 */
export function canvasFilterConditions(filter: CanvasFilter, datasets: ReadonlyArray<string> = []): CanvasFilterCondition[] {
  const predicate = canvasFilterPredicate(filter);
  const field = filter.field?.trim();
  if (!predicate || !field) return [];
  const sets = [...new Set([filter.entity?.trim(), ...datasets.map((dataset) => dataset?.trim())].filter((dataset): dataset is string => !!dataset))];
  return sets.map((dataset) => ({ dataset, field, operator: predicate.operator, value: predicate.value }));
}

/** Stored / imported canvas filters, cleaned (unknown keys dropped, ids unique). */
export function normalizeCanvasFilters(raw: unknown): CanvasFilter[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const strings = (list: unknown) => (Array.isArray(list) ? list : []).map((item) => String(item ?? '').trim()).filter(Boolean);
  return raw.filter((item): item is Record<string, unknown> => !!item && typeof item === 'object').map((item) => {
    let id = String(item['id'] ?? '').trim();
    if (!id || seen.has(id)) id = createCanvasFilter().id;
    seen.add(id);
    return {
      id,
      entity: String(item['entity'] ?? '').trim(),
      field: String(item['field'] ?? '').trim(),
      operator: String(item['operator'] ?? 'eq').trim().toLowerCase() || 'eq',
      values: strings(item['values']),
      targets: [...new Set(strings(item['targets']))],
      ...(item['disabled'] === true ? { disabled: true } : {}),
    };
  });
}
